/**
 * 批量新增柜子 - many new Containers at once, each with its own details, or one
 * row at a time (Alan, 2026-09-26: "可以一次性多个柜子和个个信息批量的加入进去表格。
 * 一个专门的tab这个页面可以批量上传新的柜子或者单独一个个的").
 *
 * Two steps, not one grid with everything mixed in (Alan, 2026-09-28: "数字类型的
 * 可以直接批量粘贴然后有些特殊的信息之类的可以去选比如状态...点击下一步会出现预先表格
 * ...user需要在选择那些细致的选项比如需要下拉...确认无误后才点击然后批量添加到系统"):
 *
 *  - Step 1 (粘贴/paste) is the Container #s and nothing else (Alan, 2026-09-30: "第一步
 *    只让黏贴柜号点击下一步才是给每一个柜子增加信息"). A sheet pasted with its own header
 *    row still lands its other columns on the rows - they show up in step 2.
 *  - Step 2 (确认/confirm) is where each Container gets its information: customer, size,
 *    delivery address (that customer's approved ones), SSL, terminal, status, own chassis
 *    as pickers - each pre-selected from whatever was
 *    pasted for it (a matched customer name, a recognised size, a typed SSL that is or
 *    isn't in the usual list), left to a blank "choose" for the columns that have no
 *    free-text form (status, own chassis) rather than guessed - and MBL, weight,
 *    ETA, LFD and note as plain cells. Nothing is added to the system until this
 *    step's own button is pressed.
 *
 * `column.confirmSelect` on BULK_ADD_COLUMNS marks which columns are pickers; every
 * column but the Container # belongs to step 2 (`isStepTwoColumn`). Both steps share one
 * table, one set of rows and one validation pass (checkBulkAddRows) - the wizard only
 * changes which cells are visible, never what is checked.
 *
 * Nothing new on the server. Each Container is made the way Add Task makes it -
 * createOperationsContainerTasks, one call per customer and B/L, which is one
 * Task with its Containers as Add Task would have filed it - and the details Add
 * Task does not take (shipping line, terminal, ETA, LFD, status, note, chassis,
 * own chassis) are then written the way a cell edit writes them, one field at a
 * time through updateMyContainerDashboardField, so each lands with the cell's own
 * permission check, revision and audit line. A role that may not write one of
 * these columns has that field skipped, with a warning - never a refused row.
 *
 * Whether a number is already in the system is asked of the rows the Dashboard
 * has already loaded (container-row-index), not of Firestore: the rules let only
 * Admin and Dispatch read a raw Container, so a clerk's query would be refused. A
 * number that is not loaded is still caught - the callable refuses a taken number
 * and names it, and the row turns red then.
 *
 * Two things the rows are held to that Add Task itself is not:
 *  - The customer must already exist. Add Task quietly opens a PROVISIONAL
 *    customer for a name it does not know, so one mistyped name in a pasted sheet
 *    would have become a new company. Here it is a red row instead.
 *  - The Container # must look like one: four letters and seven digits. The
 *    callable takes any string without spaces, which is how "SEKU6504656SH" -
 *    the number with its customer typed onto the end - would have gone in.
 */

import { columnEditableForRole, fieldOptions } from './container-field-registry.mjs?v=20260806.100';
import { containerStatusLabel } from './container-status-presentation.mjs?v=20260806.100';
import { parseBulkValue } from './bulk-value.mjs?v=20260806.100';
import { weightToPounds } from './weight-unit.mjs?v=20260806.100';
import { dashboardAutosaveErrorCode } from './dashboard-field-autosave.mjs?v=20260806.100';

const CONTAINER_NUMBER = /^[A-Z]{4}\d{7}$/;
const MAX_CONTAINERS_PER_TASK = 50;

// The grid's columns, in the order a pasted sheet without a header is read in - which is
// also the order they are drawn in (Alan, 2026-10-01: "Container #, customer name, chs, pin,
// FCST, type, weight, ssl, term, note, eta, lfd ... 批量上传的表格是按照这个顺序"). A column
// that is a menu in the main table is a menu here (`confirmSelect`), pre-selected from
// whatever a pasted sheet carried for it. Delivery Address, MBL and Status are not in his list;
// they stay, after LFD (Address sits beside Customer: "客户选择的什么这个地址选项就会出现对应
// 的地址"), because each does a job Add Task already had.
// `phase: 'create'` goes in with the Container; `phase: 'field'` is written after.
export const BULK_ADD_COLUMNS = Object.freeze([
  { key: 'container_no', zh: '柜号', en: 'Container #', phase: 'create', required: true,
    aliases: ['柜号', 'container', 'container #', 'container no', 'cntr', 'container number'], frozen: true },
  // ETA and MBL sit beside the Container # and, with it, stay put while the sheet scrolls sideways (Alan,
  // 2026-10-12: "上面的柜号 ETA MBL 能否冻结"). They moved from after Term / LFD to get here, so a header-less
  // paste across the whole sheet is read in this new drawn order.
  { key: 'eta_at', zh: 'ETA', en: 'ETA', phase: 'field', aliases: ['eta', '到港', '到港日'], frozen: true },
  { key: 'master_bl_no', zh: '提单号', en: 'MBL', phase: 'create',
    aliases: ['提单号', '提单', 'mbl', 'b/l', 'bl', 'master bl', 'master b/l'], frozen: true },
  { key: 'customer_name', zh: '客户', en: 'Customer', phase: 'create', required: true, pickInConfirm: true,
    aliases: ['客户', 'customer', '客户名', 'customer name'] },
  // Whether the customer brings their own chassis (the CHS column of the main table: yes / no).
  // The Chassis # text column was removed (Alan, 2026-10-01: "这个里面的这个column移除").
  { key: 'customer_chassis', zh: 'CHS', en: 'CHS', phase: 'field', confirmSelect: true,
    aliases: ['chs', '自带车架', '自带车架号', 'own chassis', 'customer chassis', 'customer’s own chassis'] },
  { key: 'pin', zh: 'PIN', en: 'PIN', phase: 'field', aliases: ['pin'] },
  { key: 'pre_alert_status', zh: 'FCST', en: 'FCST', phase: 'field', confirmSelect: true,
    aliases: ['fcst', 'forecast', 'forecast status', 'pre-alert', 'pre alert', 'pre-alert status'] },
  { key: 'size_type', zh: '柜型', en: 'Type', phase: 'create', confirmSelect: true,
    aliases: ['柜型', 'size', 'type', 'size/type'] },
  { key: 'weight_lbs', zh: '重量', en: 'Weight', phase: 'create',
    aliases: ['重量', 'weight', 'weight (lbs)', 'lbs', 'kg'] },
  { key: 'ssl', zh: '船司', en: 'SSL', phase: 'field', confirmSelect: true,
    aliases: ['船司', 'ssl', 'carrier', 'shipping line'] },
  { key: 'terminal_name', zh: '码头', en: 'Term', phase: 'field', confirmSelect: true,
    aliases: ['码头', 'terminal', 'term'] },
  { key: 'hold_note', zh: '备注', en: 'Note', phase: 'field', aliases: ['备注', 'note', 'hold', 'hold note', 'notes'] },
  { key: 'lfd_at', zh: 'LFD', en: 'LFD', phase: 'field', aliases: ['lfd', '免租', '免租期'] },
  // A picker over THAT customer's approved addresses, loaded the way Add Task loads them; a
  // pasted address that is not on the list stays as typed.
  { key: 'delivery_warehouse_address', zh: '送货地址', en: 'Delivery Address', phase: 'create', pickInConfirm: true,
    aliases: ['送货地址', '地址', 'address', 'delivery address', '收货地址'] },
  { key: 'ocean_status', zh: '状态', en: 'Status', phase: 'field', confirmSelect: true,
    aliases: ['状态', 'status', 'ocean status', '海柜状态'] },
]);

// A header-less paste is read in the drawn order, pickers included: the text lands in the
// row and the picker comes up pre-selected from it (or red, if it matched nothing).
export const BULK_ADD_PASTE_COLUMNS = BULK_ADD_COLUMNS;

// Step 1 asks for the Container #s alone; every other column is filled in step 2.
export const isStepTwoColumn = (column) => column.key !== 'container_no';

const clean = (value) => String(value ?? '').replace(/ /g, ' ').trim();
const fold = (value) => clean(value).toLowerCase().replace(/\s+/g, ' ');
const containerNumber = (value) => clean(value).replace(/\s+/g, '').toUpperCase();

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

export const emptyBulkAddRow = () => Object.fromEntries(BULK_ADD_COLUMNS.map(({ key }) => [key, '']));

/**
 * Pasted text as rows. A first line whose cells are mostly column names is a
 * header, and the rest is read by it - so a sheet's own columns, in its own
 * order, go where they belong and anything it has that this does not is left out.
 * A header is matched against every column, picked or not, because a sheet with
 * its own "状态" column should still land there. Without a header, cells are read
 * in `columns`' own order starting at `startColumn` - step 1's columns by
 * default, since that positional reading only ever makes sense against what a
 * pasted sheet can actually carry (no one pastes a column for a picker).
 */
export function parseBulkAddPaste(text, { startColumn = 0, columns = BULK_ADD_PASTE_COLUMNS } = {}) {
  const lines = String(text ?? '').replace(/\r\n?/g, '\n').split('\n');
  while (lines.length && !clean(lines[lines.length - 1])) lines.pop();
  if (!lines.length) return { header: false, rows: [] };
  const cells = lines.map((line) => line.split('\t'));
  const headerKeys = cells[0].map((cell) => (
    BULK_ADD_COLUMNS.find(({ aliases }) => aliases.includes(fold(cell)))?.key ?? null
  ));
  const recognised = headerKeys.filter(Boolean).length;
  const header = recognised >= 2 && recognised >= Math.ceil(cells[0].filter((cell) => clean(cell)).length / 2);
  const body = header ? cells.slice(1) : cells;
  const rows = body.map((line) => {
    const row = {};
    line.forEach((cell, index) => {
      const key = header ? headerKeys[index] : columns[startColumn + index]?.key;
      if (key) row[key] = clean(cell);
    });
    return row;
  });
  return { header, rows };
}

/** The sizes the callable takes, from how people write them. Null when it cannot tell. */
export function normalizeBulkSize(raw) {
  const text = clean(raw).toUpperCase().replace(/['’\s]/g, '');
  if (!text) return '';
  if (text === '20' || text === '20GP' || text === '20DC' || text === '20.0') return '20';
  if (text === '40' || text === '40GP' || text === '40DC' || text === '40.0') return '40';
  if (text === '40HQ' || text === '40HC' || text === '40H') return '40HQ';
  if (text === '45' || text === '45HQ' || text === '45HC' || text === '45.0') return '45';
  if (text === 'CUSTOM') return 'CUSTOM';
  return null;
}

// Pounds, from pounds or kilograms - the unit the column header is set to, or the one written
// after the number (Alan, 2026-10-01: "这个weight要可以转换kg和lb").
function normalizeWeight(raw, unit) {
  return weightToPounds(raw, unit);
}

/** Customers by every name they answer to, folded, pointing at the name the callable matches on. */
export function bulkAddCustomerIndex(customers) {
  const index = new Map();
  for (const customer of Array.isArray(customers) ? customers : []) {
    if (!customer || customer.active !== true || customer.archived_by_restore === true) continue;
    const name = clean(customer.name);
    if (!name) continue;
    for (const alias of [name, customer.short_name, ...(Array.isArray(customer.aliases) ? customer.aliases : [])]) {
      const key = fold(alias);
      if (key && !index.has(key)) index.set(key, name);
    }
  }
  return index;
}

const MESSAGES = Object.freeze({
  zh: {
    number: '柜号格式不对（4 个字母 + 7 位数字）', numberMissing: '缺柜号', twice: '这批里有两行同一个柜号',
    exists: '系统里已经有这个柜号', customerMissing: '缺客户', customer: '系统里没有这个客户',
    size: '柜型看不懂（20 / 40 / 40HQ / 45）', weight: '重量不是数字',
    weightConverted: (from, pounds) => `${from} → ${pounds} lb`,
    field: (label) => `${label}看不懂`, role: (label) => `你的角色不能填${label}，会跳过`,
  },
  en: {
    number: 'Container # must be 4 letters and 7 digits', numberMissing: 'Container # missing', twice: 'This Container # is on two rows',
    exists: 'Already in the system', customerMissing: 'Customer missing', customer: 'No such customer',
    size: 'Size not understood (20 / 40 / 40HQ / 45)', weight: 'Weight is not a number',
    weightConverted: (from, pounds) => `${from} → ${pounds} lb`,
    field: (label) => `${label} not understood`, role: (label) => `Your role cannot set ${label}; skipped`,
  },
});

const messagesFor = (locale) => (String(locale).toLowerCase().startsWith('zh') ? MESSAGES.zh : MESSAGES.en);
const labelFor = (column, locale) => (String(locale).toLowerCase().startsWith('zh') ? column.zh : column.en);

/**
 * Every row checked: `blank` is ignored, `error` cannot be added and says why,
 * `ready` can. A ready row may still carry warnings - a column this role may not
 * write is skipped, not refused, because the Container is still worth adding.
 */
export function checkBulkAddRows(rows, {
  customers = new Map(), existing = new Set(), role = '', registry = [], locale = 'zh', now = Date.now(),
  weightUnit = 'lb',
} = {}) {
  const text = messagesFor(locale);
  const registryByKey = new Map(registry.map((column) => [column.key, column]));
  const counts = new Map();
  for (const row of rows) {
    const number = containerNumber(row.container_no);
    if (number) counts.set(number, (counts.get(number) ?? 0) + 1);
  }
  return rows.map((row) => {
    const filled = BULK_ADD_COLUMNS.some(({ key }) => clean(row[key]));
    if (!filled) return { status: 'blank', errors: [], warnings: [] };
    const errors = [];
    const warnings = [];
    const number = containerNumber(row.container_no);
    if (!number) errors.push(text.numberMissing);
    else if (!CONTAINER_NUMBER.test(number)) errors.push(text.number);
    else if (counts.get(number) > 1) errors.push(text.twice);
    else if (existing.has(number)) errors.push(text.exists);
    const customerName = clean(row.customer_name) ? customers.get(fold(row.customer_name)) ?? null : null;
    if (!clean(row.customer_name)) errors.push(text.customerMissing);
    else if (!customerName) errors.push(text.customer);
    const size = normalizeBulkSize(row.size_type);
    if (size === null) errors.push(text.size);
    const weight = normalizeWeight(row.weight_lbs, weightUnit);
    if (weight.error) errors.push(text.weight);
    else if (weight.converted) warnings.push(text.weightConverted(weight.from, weight.value));
    const fields = [];
    for (const column of BULK_ADD_COLUMNS.filter(({ phase }) => phase === 'field')) {
      if (!clean(row[column.key])) continue;
      const registryColumn = registryByKey.get(column.key);
      if (!registryColumn || !columnEditableForRole(registryColumn, role)) {
        warnings.push(text.role(labelFor(column, locale)));
        continue;
      }
      const parsed = parseBulkValue(registryColumn, row[column.key], { now });
      if (parsed.error) errors.push(text.field(labelFor(column, locale)));
      else if (!parsed.skip) fields.push({ field: column.key, value: parsed.value });
    }
    if (errors.length) return { status: 'error', errors, warnings };
    return {
      status: 'ready',
      errors,
      warnings,
      containerNo: number,
      create: {
        customer_name: customerName,
        master_bl_no: clean(row.master_bl_no),
        delivery_warehouse_address: clean(row.delivery_warehouse_address),
        container: {
          container_no: number,
          ...(size ? { size } : {}),
          ...(weight.value !== null ? { weight: weight.value } : {}),
        },
      },
      fields,
    };
  });
}

/**
 * Rows grouped into Tasks the way Add Task files them: one customer, one B/L, one
 * address, its Containers together. A row with no B/L is a Task of its own -
 * two unrelated Containers must not become one shipment because both left the
 * column blank.
 */
export function groupBulkAddTasks(checked) {
  const groups = new Map();
  const tasks = [];
  checked.forEach((entry, index) => {
    if (entry.status !== 'ready') return;
    const { customer_name: customer, master_bl_no: bl, delivery_warehouse_address: address } = entry.create;
    const key = bl ? JSON.stringify([customer, bl, address]) : null;
    const existingTask = key ? groups.get(key) : null;
    if (existingTask && existingTask.rows.length < MAX_CONTAINERS_PER_TASK) {
      existingTask.rows.push(index);
      return;
    }
    const task = { customer_name: customer, master_bl_no: bl, delivery_warehouse_address: address, rows: [index] };
    tasks.push(task);
    if (key) groups.set(key, task);
  });
  return tasks;
}

/**
 * Which id the callable gave which Container. createOperationsContainerTasks
 * returns containerIds in its plan's order, and the plan sorts the Containers by
 * number with localeCompare - so the same sort here pairs them, with no read.
 */
export function bulkAddCreatedIds(task, checked, containerIds) {
  const numbers = task.rows.map((index) => checked[index].containerNo)
    .sort((left, right) => left.localeCompare(right));
  const ids = Array.isArray(containerIds) ? containerIds : [];
  return numbers.length === ids.length ? new Map(numbers.map((number, index) => [number, ids[index]])) : new Map();
}

/** The numbers a refusal says are taken: "TGBU1234567 (SINO)" -> TGBU1234567. */
export function bulkAddTakenNumbers(error) {
  const taken = error?.details?.taken;
  return (Array.isArray(taken) ? taken : [])
    .map((entry) => containerNumber(String(entry).split('(')[0]))
    .filter((number) => CONTAINER_NUMBER.test(number));
}

/** The callable's own request for one Task. */
export function bulkAddTaskRequest(task, checked, requestId) {
  return {
    customer_name: task.customer_name,
    request_id: requestId,
    ...(task.master_bl_no ? { master_bl_no: task.master_bl_no } : {}),
    ...(task.delivery_warehouse_address ? { delivery_warehouse_address: task.delivery_warehouse_address } : {}),
    containers: task.rows.map((index) => checked[index].create.container),
  };
}

const COPY = Object.freeze({
  zh: {
    title: '批量新增柜子', addRow: '加一行', clear: '清空', paste: '从 Excel 粘贴',
    next: '下一步', back: '上一步', pick: '请选择', pickCustomerFirst: '先选客户', noAddress: '这个客户没有存地址', quote: '报价',
    submit: (count) => `新增 ${count} 个柜子`,
    ready: '可以新增', creating: '新增中…', created: '已新增', failed: '失败', partly: (fields) => `已新增，${fields} 没写进去`,
    summary: (ready, problems) => (problems ? `可新增 ${ready} · 有问题 ${problems}` : `可新增 ${ready}`),
    done: (created, failed) => (failed ? `已新增 ${created}，失败 ${failed}` : `已新增 ${created}`),
    remove: '删除这一行', status: '检查', custom: '自定义',
    nothingToAdd: '没有可新增的柜子 — 这几行已经新增过了，或还有行没填完', stillRunning: '还在新增中，请稍等…',
  },
  en: {
    title: 'Bulk Add Containers', addRow: 'Add row', clear: 'Clear', paste: 'Paste from Excel',
    next: 'Next', back: 'Back', pick: 'Choose one', pickCustomerFirst: 'Choose the customer first', noAddress: 'No saved address for this customer', quote: 'Quote',
    submit: (count) => `Add ${count} Container${count === 1 ? '' : 's'}`,
    ready: 'Ready', creating: 'Adding…', created: 'Added', failed: 'Failed', partly: (fields) => `Added; ${fields} not saved`,
    summary: (ready, problems) => (problems ? `${ready} ready · ${problems} with problems` : `${ready} ready`),
    done: (created, failed) => (failed ? `${created} added, ${failed} failed` : `${created} added`),
    remove: 'Remove this row', status: 'Check', custom: 'Custom',
    nothingToAdd: 'Nothing to add - these rows were already added, or a row is unfinished', stillRunning: 'Still adding, one moment…',
  },
});

const copyFor = (locale) => (String(locale).toLowerCase().startsWith('zh') ? COPY.zh : COPY.en);

export function bulkAddPageMarkup({ locale = 'zh' } = {}) {
  const copy = copyFor(locale);
  const quoteHead = `<th scope="col" data-bulk-add-column="quote" data-bulk-add-confirm-column hidden>${escapeHtml(copy.quote)}</th>`;
  const head = BULK_ADD_COLUMNS.map((column) => (
    `<th scope="col" data-bulk-add-column="${column.key}"${isStepTwoColumn(column) ? ' data-bulk-add-confirm-column hidden' : ''}${column.frozen ? ` data-bulk-add-frozen="${column.key}"` : ''}>${escapeHtml(labelFor(column, locale))}${column.required ? '<span aria-hidden="true">*</span>' : ''}${column.key === 'weight_lbs' ? ' <select class="bulk-add__unit" data-bulk-add-weight-unit aria-label="Weight unit"><option value="lb">lb</option><option value="kg">kg</option></select>' : ''}</th>`
  )).map((cell, index) => (BULK_ADD_COLUMNS[index].key === 'delivery_warehouse_address' ? cell + quoteHead : cell)).join('');
  return `<section class="bulk-add" data-bulk-add data-bulk-add-step="paste">
      <div class="bulk-add__head">
        <h2>${escapeHtml(copy.title)}</h2>
        <div class="bulk-add__actions">
          <button type="button" data-bulk-add-row>${escapeHtml(copy.addRow)}</button>
          <button type="button" data-bulk-add-clear>${escapeHtml(copy.clear)}</button>
          <span class="bulk-add__summary" data-bulk-add-summary aria-live="polite"></span>
          <button type="button" class="bulk-add__submit" data-bulk-add-back hidden>${escapeHtml(copy.back)}</button>
          <button type="button" class="bulk-add__submit" data-bulk-add-next>${escapeHtml(copy.next)}</button>
          <button type="button" class="bulk-add__submit" data-bulk-add-submit disabled hidden>${escapeHtml(copy.submit(0))}</button>
        </div>
      </div>
      <div class="bulk-add__grid">
        <table class="bulk-add__table">
          <thead><tr><th scope="col" data-bulk-add-frozen="line">#</th>${head}<th scope="col">${escapeHtml(copy.status)}</th><th scope="col"><span class="visually-hidden">${escapeHtml(copy.remove)}</span></th></tr></thead>
          <tbody data-bulk-add-rows></tbody>
        </table>
      </div>
    </section>`;
}

/**
 * A step-1 column's plain text box, or a step-2 column's picker. The picker
 * always exists (hidden by `column.confirmSelect` in the head, and by the
 * `hidden` attribute baked in here from `wizardStep`) rather than being built
 * only when reached, so switching steps never has to fetch or rebuild anything.
 */
function selectCellOptions(column, row, { customerIndex, customerNames, registryByKey, locale, copy, addressCache }) {
  const raw = clean(row[column.key]);
  if (column.key === 'customer_name') {
    const matched = raw ? customerIndex.get(fold(raw)) ?? null : null;
    return { selected: matched ?? '', placeholder: copy.pick, options: customerNames.map((name) => [name, name]) };
  }
  if (column.key === 'delivery_warehouse_address') {
    const customer = customerIndex.get(fold(row.customer_name)) ?? '';
    const list = customer ? addressCache.get(customer) ?? null : null;
    const known = (list ?? []).map((entry) => [entry.address, entry.label]);
    const typed = clean(raw);
    const match = typed ? known.find(([value]) => fold(value) === fold(typed)) : null;
    const options = [...known];
    if (typed && !match) options.push([typed, `${copy.custom}: ${typed}`]);
    // Still loading (null) reads as the ordinary prompt; only a loaded empty list says none.
    const addressPrompt = !customer ? copy.pickCustomerFirst : (list && !list.length ? copy.noAddress : copy.pick);
    return { selected: match ? match[0] : typed, placeholder: addressPrompt, options };
  }
  if (column.key === 'size_type') {
    const matched = normalizeBulkSize(raw);
    const values = ['20', '40', '40HQ', '45', 'CUSTOM'];
    return { selected: matched && values.includes(matched) ? matched : '', placeholder: copy.pick, options: values.map((value) => [value, value]) };
  }
  if (column.key === 'customer_chassis') {
    const token = fold(raw);
    const selected = /^(y(es)?|是|自带)/.test(token) ? 'yes' : /^(n(o)?|否|不)/.test(token) ? 'no' : '';
    const zh = String(locale).toLowerCase().startsWith('zh');
    return {
      selected, placeholder: copy.pick,
      options: [['yes', zh ? '是（客户自带）' : 'Yes — customer’s own'], ['no', zh ? '否（我们分配）' : 'No — we assign one']],
    };
  }
  const registryColumn = registryByKey.get(column.key) ?? {};
  const known = fieldOptions(registryColumn).filter((value) => value !== 'CUSTOM');
  if (column.key === 'ocean_status') {
    const zh = String(locale).toLowerCase().startsWith('zh');
    const wanted = fold(raw.replace(/^\d+/u, ''));
    const matched = wanted ? known.find((option) => (
      [option, containerStatusLabel(option, 'en'), containerStatusLabel(option, 'zh')].some((name) => fold(name) === wanted)
    )) : null;
    return {
      selected: matched ?? '', placeholder: copy.pick,
      options: known.map((value) => [value, containerStatusLabel(value, zh ? 'zh' : 'en')]),
    };
  }
  // ssl / terminal_name: a real menu of the usual answers, plus - only if what
  // was typed does not match one of them - the typed text itself, so a value a
  // pasted sheet already carried is never silently replaced by a blank pick.
  // The blank choice still has to be a real <option> even here: a <select> with
  // no option carrying the empty value falls back to its FIRST option the
  // instant it is drawn, and a row nobody touched would silently pick up
  // whatever line happens to be first in this list.
  const matched = raw ? known.find((option) => fold(option) === fold(raw)) : null;
  const options = known.map((value) => [value, value]);
  if (raw && !matched) options.push([raw, `${copy.custom}: ${raw}`]);
  return { selected: matched ?? (raw || ''), placeholder: copy.pick, options };
}

function pickerOptionsMarkup(placeholder, options, selected) {
  const blank = placeholder ? `<option value="">${escapeHtml(placeholder)}</option>` : '';
  return blank + options.map(([value, optionLabel]) => (
    `<option value="${escapeHtml(value)}"${value === selected ? ' selected' : ''}>${escapeHtml(optionLabel)}</option>`
  )).join('');
}

function cellMarkup(column, row, rowIndex, columnIndex, locale, placeholderHint, wizardStep, selectCtx) {
  const label = escapeHtml(labelFor(column, locale));
  // A step-2 cell stays out of sight until step 2. pickInConfirm (customer) is a picker
  // wherever it is drawn: it has no step-1 cell to type in any more.
  const asPicker = column.confirmSelect || column.pickInConfirm;
  const hidden = isStepTwoColumn(column) && wizardStep !== 'confirm' ? ' hidden' : '';
  const stepTwoAttr = (isStepTwoColumn(column) ? ' data-bulk-add-confirm-column' : '')
    + (column.frozen ? ` data-bulk-add-frozen="${column.key}"` : '');
  if (asPicker) {
    const { selected, placeholder, options } = selectCellOptions(column, row, selectCtx);
    return `<td${stepTwoAttr}${hidden}><select data-bulk-add-cell="${column.key}" data-row="${rowIndex}" data-column="${columnIndex}" aria-label="${label} ${rowIndex + 1}">${pickerOptionsMarkup(placeholder, options, selected)}</select></td>`;
  }
  const hint = rowIndex === 0 && columnIndex === 0 && placeholderHint ? ` placeholder="${escapeHtml(selectCtx.copy.paste)}"` : '';
  return `<td${stepTwoAttr}${hidden}><input type="text" data-bulk-add-cell="${column.key}" data-row="${rowIndex}" data-column="${columnIndex}" value="${escapeHtml(row[column.key] ?? '')}"${hint} autocomplete="off" spellcheck="false" aria-label="${label} ${rowIndex + 1}"></td>`;
}

/**
 * The price an approved address was quoted at, from the customer's imported quote list (Alan,
 * 2026-10-01: "本身导入表格的报价...当选择的时候自动在显示出来"). Shown, never typed; a role the server
 * keeps prices from gets the address with the money blanked, so its cell stays empty.
 */
export function bulkAddQuoteFor(row, { customerIndex, addressCache }) {
  const customer = customerIndex.get(fold(row?.customer_name)) ?? '';
  const address = fold(row?.delivery_warehouse_address);
  const entry = customer && address
    ? (addressCache.get(customer) ?? []).find((candidate) => fold(candidate.address) === address) : null;
  return { display: entry?.quote ?? '', terms: entry?.terms ?? '' };
}

function rowMarkup(row, index, locale, placeholder, wizardStep, selectCtx) {
  const copy = selectCtx.copy;
  const cells = BULK_ADD_COLUMNS.map((column, columnIndex) => {
    const cell = cellMarkup(column, row, index, columnIndex, locale, placeholder, wizardStep, selectCtx);
    if (column.key !== 'delivery_warehouse_address') return cell;
    const quote = bulkAddQuoteFor(row, selectCtx);
    const hidden = wizardStep !== 'confirm' ? ' hidden' : '';
    return `${cell}<td class="bulk-add__quote" data-bulk-add-confirm-column${hidden} data-bulk-add-quote="${index}" title="${escapeHtml(quote.terms)}">${escapeHtml(quote.display)}</td>`;
  }).join('');
  return `<tr data-bulk-add-row-index="${index}"><td class="bulk-add__line" data-bulk-add-frozen="line">${index + 1}</td>${cells}<td class="bulk-add__state" data-bulk-add-state="${index}"></td><td><button type="button" class="bulk-add__remove" data-bulk-add-remove="${index}" aria-label="${escapeHtml(copy.remove)}">×</button></td></tr>`;
}

/**
 * The page. Everything that touches Firebase is handed in, so the grid and its
 * rules run the same under a test as in the app.
 */
export function mountBulkAddContainers({
  host,
  role,
  locale = () => 'zh',
  customers = [],
  registry = [],
  checkExisting = async () => new Set(),
  loadAddresses = async () => [],
  createTask,
  updateField,
  requestId = () => globalThis.crypto?.randomUUID?.() ?? `bulk-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  now = () => Date.now(),
}) {
  if (!host || typeof createTask !== 'function' || typeof updateField !== 'function') {
    throw new TypeError('Bulk add options are invalid.');
  }
  const activeLocale = typeof locale === 'function' ? locale() : locale;
  const copy = copyFor(activeLocale);
  const customerIndex = bulkAddCustomerIndex(customers);
  const customerNames = [...new Set(customers.filter((customer) => customer?.active === true
    && customer.archived_by_restore !== true).map((customer) => clean(customer.name)).filter(Boolean))];
  const registryByKey = new Map(registry.map((column) => [column.key, column]));
  // A customer's approved addresses, by canonical name: asked once, painted into that
  // customer's rows in place (a full redraw would close a picker someone has open).
  const addressCache = new Map();
  const addressAsked = new Set();
  const selectCtx = { customerIndex, customerNames, registryByKey, locale: activeLocale, copy, addressCache };
  const addressColumn = BULK_ADD_COLUMNS.find((column) => column.key === 'delivery_warehouse_address');
  const customerOf = (row) => customerIndex.get(fold(row?.customer_name)) ?? '';

  function paintAddressCell(index) {
    const select = body.querySelector(`select[data-row="${index}"][data-bulk-add-cell="delivery_warehouse_address"]`);
    if (!select || !rows[index]) return;
    const { selected, placeholder, options } = selectCellOptions(addressColumn, rows[index], selectCtx);
    select.innerHTML = pickerOptionsMarkup(placeholder, options, selected);
    paintQuoteCell(index);
  }

  function paintQuoteCell(index) {
    const cell = body.querySelector(`[data-bulk-add-quote="${index}"]`);
    if (!cell || !rows[index]) return;
    const quote = bulkAddQuoteFor(rows[index], selectCtx);
    cell.textContent = quote.display;
    cell.title = quote.terms;
  }

  function ensureAddresses(customer) {
    if (!customer || addressAsked.has(customer)) return;
    addressAsked.add(customer);
    Promise.resolve(loadAddresses(customer))
      .then((list) => {
        addressCache.set(customer, (Array.isArray(list) ? list : [])
          .map((entry) => ({
            address: clean(entry?.address),
            label: clean(entry?.selection_label) || clean(entry?.address),
            quote: clean(entry?.quote_display),
            terms: clean(entry?.quote_terms),
          }))
          .filter((entry) => entry.address));
      })
      .catch(() => { addressAsked.delete(customer); })
      .then(() => rows.forEach((row, index) => { if (customerOf(row) === customer) paintAddressCell(index); }));
  }
  host.innerHTML = bulkAddPageMarkup({ locale: activeLocale });
  const wizard = host.querySelector('[data-bulk-add]');
  const body = host.querySelector('[data-bulk-add-rows]');
  const summary = host.querySelector('[data-bulk-add-summary]');
  const submit = host.querySelector('[data-bulk-add-submit]');
  const nextButton = host.querySelector('[data-bulk-add-next]');
  const backButton = host.querySelector('[data-bulk-add-back]');
  // Paste positions are read against BULK_ADD_PASTE_COLUMNS (the drawn order),
  // but every cell still carries its index in the full BULK_ADD_COLUMNS list (so
  // focus can find it back); this maps one to the other.
  const pasteColumnIndex = new Map(
    BULK_ADD_COLUMNS.map((column, index) => [index, BULK_ADD_PASTE_COLUMNS.indexOf(column)]),
  );
  let rows = [emptyBulkAddRow(), emptyBulkAddRow(), emptyBulkAddRow()];
  // Per row: undefined (not sent), 'creating', 'created', 'failed' - plus a note.
  let outcomes = [];
  let checked = [];
  let wizardStep = 'paste';
  // What a bare number in the Weight column means; a unit typed after it wins.
  let weightUnit = 'lb';
  const existing = new Set();
  const asked = new Set();
  let running = false;
  let existingTimer = null;

  const locked = (index) => ['creating', 'created'].includes(outcomes[index]?.state);

  function applyWizardStep() {
    if (wizard) wizard.dataset.bulkAddStep = wizardStep;
    host.querySelectorAll('thead [data-bulk-add-confirm-column]').forEach((cell) => { cell.hidden = wizardStep !== 'confirm'; });
    if (nextButton) nextButton.hidden = wizardStep === 'confirm';
    if (backButton) backButton.hidden = wizardStep === 'paste';
    if (submit) submit.hidden = wizardStep === 'paste';
  }

  // The frozen columns (line number, Container #, ETA, MBL) are sticky; each one's left offset is the drawn
  // width of the frozen columns before it - measured, since the cells size themselves.
  function pinFrozenColumns() {
    const heads = [...host.querySelectorAll('thead [data-bulk-add-frozen]')].filter((cell) => !cell.hidden);
    let left = 0;
    heads.forEach((head, position) => {
      host.querySelectorAll(`[data-bulk-add-frozen="${head.dataset.bulkAddFrozen}"]`).forEach((cell) => {
        cell.style.left = `${left}px`;
        cell.toggleAttribute('data-bulk-add-frozen-last', position === heads.length - 1);
      });
      left += head.getBoundingClientRect().width;
    });
  }

  function paintStates() {
    checked = checkBulkAddRows(rows, {
      customers: customerIndex, existing, role, registry, locale: activeLocale, now: now(), weightUnit,
    });
    let ready = 0;
    let problems = 0;
    checked.forEach((entry, index) => {
      const cell = host.querySelector(`[data-bulk-add-state="${index}"]`);
      const outcome = outcomes[index];
      let text = '';
      let tone = '';
      if (outcome) {
        text = outcome.note || copy[outcome.state] || '';
        tone = outcome.state;
      } else if (entry.status === 'error') {
        text = entry.errors.join('；');
        tone = 'error';
        problems += 1;
      } else if (entry.status === 'ready') {
        text = entry.warnings.length ? `${copy.ready} · ${entry.warnings.join('；')}` : copy.ready;
        tone = entry.warnings.length ? 'warning' : 'ready';
        ready += 1;
      }
      if (cell) {
        cell.textContent = text;
        cell.dataset.tone = tone;
      }
    });
    if (!running && summary) summary.textContent = ready || problems ? copy.summary(ready, problems) : '';
    if (submit) {
      submit.disabled = running || ready < 1;
      submit.textContent = copy.submit(ready);
    }
  }

  function render({ focus = null } = {}) {
    body.innerHTML = rows.map((row, index) => rowMarkup(row, index, activeLocale, rows.length <= 3, wizardStep, selectCtx)).join('');
    rows.forEach((_, index) => {
      if (!locked(index)) return;
      body.querySelectorAll(`[data-row="${index}"]`).forEach((input) => { input.disabled = true; });
      const remove = body.querySelector(`[data-bulk-add-remove="${index}"]`);
      if (remove) remove.disabled = true;
    });
    paintStates();
    pinFrozenColumns();
    if (focus) body.querySelector(`[data-row="${focus.row}"][data-column="${focus.column}"]`)?.focus();
    rows.forEach((row) => ensureAddresses(customerOf(row)));
    scheduleExistingCheck();
  }

  // Asked once per number, a few at a time, while the rows are being filled.
  function scheduleExistingCheck() {
    if (existingTimer) clearTimeout(existingTimer);
    existingTimer = setTimeout(async () => {
      const wanted = [...new Set(rows.map((row) => containerNumber(row.container_no)))]
        .filter((number) => CONTAINER_NUMBER.test(number) && !asked.has(number));
      if (!wanted.length) return;
      wanted.forEach((number) => asked.add(number));
      try {
        const found = await checkExisting(wanted);
        for (const number of found ?? []) existing.add(number);
      } catch {
        wanted.forEach((number) => asked.delete(number));
      }
      paintStates();
    }, 350);
  }

  body.addEventListener('input', (event) => {
    const input = event.target.closest?.('[data-bulk-add-cell]');
    if (!input) return;
    const index = Number(input.dataset.row);
    const customerBefore = customerOf(rows[index]);
    rows[index] = { ...rows[index], [input.dataset.bulkAddCell]: input.value };
    if (input.dataset.bulkAddCell === 'delivery_warehouse_address') paintQuoteCell(index);
    if (input.dataset.bulkAddCell === 'customer_name') {
      // An address belongs to the customer it was picked for; a new customer starts blank.
      const customerAfter = customerOf(rows[index]);
      if (customerAfter !== customerBefore) rows[index].delivery_warehouse_address = '';
      ensureAddresses(customerAfter);
      paintAddressCell(index);
    }
    if (outcomes[index]?.state === 'failed') outcomes[index] = undefined;
    paintStates();
    scheduleExistingCheck();
  });

  // A spreadsheet's cells arrive as tab- and line-separated text. Laid out from the
  // cell it lands in, as a spreadsheet would, or by the sheet's own header row.
  // Only a step-1 cell can start one - a picker is never where a paste begins.
  body.addEventListener('paste', (event) => {
    const input = event.target.closest?.('[data-bulk-add-cell]');
    const text = event.clipboardData?.getData('text/plain') ?? '';
    if (!input || input.tagName === 'SELECT' || !/[\t\n]/.test(text.replace(/\n+$/, ''))) return;
    event.preventDefault();
    const startRow = Number(input.dataset.row);
    const fullColumn = Number(input.dataset.column);
    const startColumn = Math.max(0, pasteColumnIndex.get(fullColumn) ?? 0);
    const parsed = parseBulkAddPaste(text, { startColumn, columns: BULK_ADD_PASTE_COLUMNS });
    parsed.rows.forEach((pasted, offset) => {
      let target = startRow + offset;
      while (locked(target)) target += 1;
      while (rows.length <= target) rows.push(emptyBulkAddRow());
      rows[target] = { ...rows[target], ...pasted };
    });
    render({ focus: { row: startRow, column: fullColumn } });
  });

  host.addEventListener('change', (event) => {
    const unit = event.target.closest?.('[data-bulk-add-weight-unit]');
    if (!unit) return;
    weightUnit = unit.value === 'kg' ? 'kg' : 'lb';
    paintStates();
  });

  host.addEventListener('click', (event) => {
    if (event.target.closest?.('[data-bulk-add-row]')) {
      rows.push(emptyBulkAddRow());
      render({ focus: { row: rows.length - 1, column: 0 } });
      return;
    }
    if (event.target.closest?.('[data-bulk-add-clear]')) {
      if (running) return;
      rows = [emptyBulkAddRow(), emptyBulkAddRow(), emptyBulkAddRow()];
      outcomes = [];
      render();
      return;
    }
    if (event.target.closest?.('[data-bulk-add-next]')) {
      if (running) return;
      wizardStep = 'confirm';
      applyWizardStep();
      render();
      return;
    }
    if (event.target.closest?.('[data-bulk-add-back]')) {
      if (running) return;
      wizardStep = 'paste';
      applyWizardStep();
      render();
      return;
    }
    const remove = event.target.closest?.('[data-bulk-add-remove]');
    if (remove && !running) {
      const index = Number(remove.dataset.bulkAddRemove);
      if (locked(index)) return;
      rows.splice(index, 1);
      outcomes.splice(index, 1);
      if (!rows.length) rows.push(emptyBulkAddRow());
      render();
    }
  });

  // Every way out of the button says something (Alan, 2026-09-30: "可以新增了为什么点击
  // 没有反应"). It used to return in silence when there was nothing to add and, if
  // anything threw once it was running, left `running` set - every press after that
  // returned at the first line, which looks exactly like a button that does nothing.
  async function submitRows() {
    if (running) {
      if (summary) summary.textContent = copy.stillRunning;
      return;
    }
    if (wizardStep !== 'confirm') return;
    const plan = checkBulkAddRows(rows, {
      customers: customerIndex, existing, role, registry, locale: activeLocale, now: now(), weightUnit,
    });
    const tasks = groupBulkAddTasks(plan).filter((task) => task.rows.every((index) => !locked(index)));
    if (!tasks.length) {
      if (summary) summary.textContent = copy.nothingToAdd;
      return;
    }
    running = true;
    host.querySelectorAll('[data-bulk-add-cell], [data-bulk-add-remove], [data-bulk-add-clear], [data-bulk-add-back]').forEach((control) => { control.disabled = true; });
    let created = 0;
    let failed = 0;
    const total = tasks.reduce((sum, task) => sum + task.rows.length, 0);
    const progress = () => { if (summary) summary.textContent = `${copy.creating} ${created + failed} / ${total}`; };
    progress();
    for (const task of tasks) {
      task.rows.forEach((index) => { outcomes[index] = { state: 'creating' }; });
      paintStates();
      let response = null;
      try {
        response = await createTask(bulkAddTaskRequest(task, plan, requestId()));
      } catch (error) {
        const taken = bulkAddTakenNumbers(error);
        if (taken.length) {
          // The whole Task was refused for these; they turn red as "already in the
          // system" and the rest of the Task can go again as it is.
          taken.forEach((number) => existing.add(number));
          task.rows.forEach((index) => { outcomes[index] = undefined; });
        } else {
          const reason = error?.message || '';
          task.rows.forEach((index) => { outcomes[index] = { state: 'failed', note: `${copy.failed}：${reason}` }; });
        }
        failed += task.rows.length;
        progress();
        paintStates();
        continue;
      }
      const ids = bulkAddCreatedIds(task, plan, response?.containerIds);
      for (const index of task.rows) {
        const entry = plan[index];
        const unsaved = [];
        const containerId = ids.get(entry.containerNo);
        // A new Container starts at revision 0, and each field bumps it by one.
        let revision = 0;
        for (const { field, value } of entry.fields) {
          try {
            if (!containerId) throw new Error('CONTAINER_ID_UNKNOWN');
            const result = await updateField({
              containerId, field, value, expectedRevision: revision, requestId: requestId(),
            });
            revision = Number.isSafeInteger(result?.informationRevision) ? result.informationRevision : revision + 1;
          } catch (error) {
            // A new Container is already created with some fields at their own
            // default (own chassis defaults to "no" - found on the emulator: a
            // fresh row explicitly picked "no" and the write came back refused
            // as a no-op). The value is already what was asked for, so this is
            // not something to tell the operator went unsaved.
            if (dashboardAutosaveErrorCode(error) !== 'NO_CHANGE') {
              unsaved.push(labelFor(BULK_ADD_COLUMNS.find(({ key }) => key === field), activeLocale));
            }
          }
        }
        outcomes[index] = unsaved.length
          ? { state: 'created', note: copy.partly(unsaved.join('、')) }
          : { state: 'created' };
        existing.add(entry.containerNo);
        created += 1;
        progress();
      }
      paintStates();
    }
    running = false;
    // render() throws away and rebuilds every row cell, so a locked row's
    // inputs start fresh already enabled - but Clear and Back live in the
    // static header, outside render()'s reach, and stay exactly as this
    // disabled them until something says otherwise (found on the emulator:
    // Back was stuck disabled after the very first successful batch).
    host.querySelectorAll('[data-bulk-add-clear], [data-bulk-add-back]').forEach((control) => { control.disabled = false; });
    render();
    if (summary) summary.textContent = copy.done(created, failed);
  }

  submit?.addEventListener('click', async () => {
    try {
      await submitRows();
    } catch (error) {
      // Whatever threw, the page is usable again and says so.
      running = false;
      host.querySelectorAll('[data-bulk-add-clear], [data-bulk-add-back]').forEach((control) => { control.disabled = false; });
      render();
      if (summary) summary.textContent = `${copy.failed}：${error?.message || ''}`;
    }
  });

  applyWizardStep();
  render();
  return Object.freeze({
    destroy() {
      if (existingTimer) clearTimeout(existingTimer);
      host.innerHTML = '';
    },
  });
}
